import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import CPPUtils from 'services/CPPUtils';
import type { Patient, PharmacyDirectory, Prescription } from 'generated/api';

/**
 * Resolves a patient's pharmacy from `default_pharmacy_id` against the live
 * directory (GET /pharmacies/{id}). Returns null when the patient has no
 * pharmacy on file OR the referenced row no longer exists — the nightly OCP
 * sync deletes closed/sold pharmacies. The id is the single source of truth;
 * there is no embedded snapshot, so this always reflects the current
 * name/fax/address.
 */
/**
 * Like {@link useDefaultPharmacy} but also reports whether resolution is still
 * in flight. Callers that preselect a value need to wait for `loading` to clear
 * before falling back, otherwise they race the async lookup and lock in the
 * fallback before the real default arrives.
 */
export function useDefaultPharmacyState(
  patient: Pick<Patient, 'default_pharmacy_id'> | undefined | null,
): { pharmacy: PharmacyDirectory | null; loading: boolean } {
  const dispatch = useAppDispatch();
  const id = patient?.default_pharmacy_id;
  // The result is tagged with the id it was resolved for, so a result belonging
  // to a previous id is never exposed (another patient's name/fax) and `loading`
  // is true in the very first commit where a new id appears — callers preselect
  // in that commit, so a one-render-late flag would let them lock in a fallback.
  const [resolved, setResolved] = useState<{
    id: string;
    pharmacy: PharmacyDirectory | null;
  } | null>(null);
  const current = id && resolved && resolved.id === id ? resolved : null;

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    // No errorTitle: a 404 (pharmacy removed) resolves to undefined without a
    // toast, which we surface as "no pharmacy on file". A rejection resolves to
    // "no pharmacy" too, so a failed lookup can't wedge `loading` forever.
    dispatch(api.Pharmacies.getPharmacy({ id }))
      .then((result?: PharmacyDirectory) => {
        if (!cancelled) setResolved({ id, pharmacy: result ?? null });
      })
      .catch(() => {
        if (!cancelled) setResolved({ id, pharmacy: null });
      });
    return () => {
      cancelled = true;
    };
  }, [id, dispatch]);

  return { pharmacy: current?.pharmacy ?? null, loading: !!id && !current };
}

export function useDefaultPharmacy(
  patient: Pick<Patient, 'default_pharmacy_id'> | undefined | null,
): PharmacyDirectory | null {
  return useDefaultPharmacyState(patient).pharmacy;
}

/** Shared empty result: callers put `pharmacies` in effect deps. */
const EMPTY_PHARMACIES: PharmacyDirectory[] = [];

/**
 * Resolves the patient's recently-used pharmacies (from RX history + their
 * default) against the live directory, returning only the ones that still exist
 * in the current CPO directory — as fresh directory rows.
 *
 * RX pharmacies are snapshots whose `id` points at a directory row that the
 * nightly OCP sync may since have replaced or removed. Offering such a stale row
 * as a pick is a trap: choosing it sets `default_pharmacy_id` to an id that
 * matches no pharmacy in the DB, so it silently fails to bind to the patient
 * (and can't be faxed). We verify each candidate with GET /pharmacies/{id} and
 * drop the misses.
 */
export function useValidPreviousPharmacies(
  prescriptions: Prescription[] | undefined | null,
  defaultPharmacy: PharmacyDirectory | null | undefined,
): { pharmacies: PharmacyDirectory[]; loading: boolean } {
  const dispatch = useAppDispatch();

  const candidates = useMemo(
    () => CPPUtils.getPreviousPharmacies(prescriptions ?? [], defaultPharmacy),
    [prescriptions, defaultPharmacy],
  );

  // Re-resolve only when the (ordered, deduped) set of candidate ids changes.
  const idsKey = useMemo(
    () =>
      _.uniq(candidates.map((p: any) => p?.id).filter((id: any): id is string => !!id)).join(','),
    [candidates],
  );

  // Tagged with the candidate set it was resolved for: rows resolved for another
  // patient must not stay on offer while the new set is in flight, and `loading`
  // must already be true in the commit where the candidates change.
  const [resolved, setResolved] = useState<{
    idsKey: string;
    pharmacies: PharmacyDirectory[];
  } | null>(null);
  const current = idsKey && resolved && resolved.idsKey === idsKey ? resolved : null;

  useEffect(() => {
    const ids = idsKey ? idsKey.split(',') : [];
    if (ids.length === 0) return undefined;
    let cancelled = false;
    // No errorTitle: a 404 (pharmacy no longer in the directory) resolves to
    // undefined without a toast, and is simply dropped. Order is preserved
    // (default first), so the resolved list keeps the candidates' order.
    Promise.all(
      ids.map((id) =>
        dispatch(api.Pharmacies.getPharmacy({ id }))
          .then((r?: PharmacyDirectory) => r)
          .catch(() => undefined),
      ),
    ).then((rows) => {
      if (cancelled) return;
      setResolved({ idsKey, pharmacies: rows.filter((p): p is PharmacyDirectory => !!p) });
    });
    return () => {
      cancelled = true;
    };
  }, [idsKey, dispatch]);

  return { pharmacies: current?.pharmacies ?? EMPTY_PHARMACIES, loading: !!idsKey && !current };
}
